/**
 * JP Gems & Jewels - Sales Order Book (persisted, shared across devices).
 * -----------------------------------------------------------------------------
 * Model mirrors the founder's Excel account sheet:
 *
 *   Party (client)  ─►  Invoice / Memo (e.g. JP-SO-0001)  ─►  Sale Lines
 *
 * The sales team picks a party and keeps appending sale lines (requirements) to
 * that party's OPEN invoice over the months. When the dues reach a peak they
 * CLOSE the invoice — it is finalised (invoice no + closed date) and a single
 * combined PDF/Excel is generated for the party. Each party works independently.
 *
 * Shape / Quality / Category / Sieve / Size / Code are pick-lists (seeded from
 * the real Excel data) so the sales team selects instead of typing — with a "+"
 * to add a new option on the spot.
 *
 * Amount columns match the sheet:
 *   less        = saleRate * discount%           (Less 6)
 *   rateAfter   = saleRate - less                (Amt After -6, per carat)
 *   saleAmt     = saleRate * weight              (line sale amount)
 *   invAmt      = saleAmt * (1 - discount%)      (Inv Amt After -6)
 *
 * Saved to the backend database via sharedStorage under "hariom-erp-salesbook".
 */
import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import { sharedStorage } from './sharedStorage'

export interface SaleLine {
  id: string
  date: string          // YYYY-MM-DD
  dueDate: string       // YYYY-MM-DD
  shape: string         // ROUND, BUGG, MQ, PRINCES, PEAR, TRILION...
  quality: string       // WH SI, TOP LB GH SI, LOWER WH...
  category: string      // RAPSHODY, VEGAS... (optional)
  sieve: string         // "Slive" sieve range, e.g. +16, -2, +2.5-3
  size: string          // secondary size, e.g. 1/5, -2.5, +7
  code: string          // lot code, e.g. DRDRAI, SAN, MOH
  pcs: number
  weight: number        // carats
  saleRate: number      // per-carat sale rate
  discountPct: number   // default 6
  remarks: string       // free note from the sales team (shown on the invoice)
  highlight?: boolean   // flag a line to clarify with the customer (shown on downloads)
}

export interface Invoice {
  id: string
  invoiceNo: string     // memo / sale no (auto JP-SO-0001, editable)
  party: string
  status: 'open' | 'closed'
  createdDate: string
  closedDate: string
  lines: SaleLine[]
  paid?: boolean        // payment received for this (closed) invoice — set via the Pay button
  paidDate?: string     // when it was marked paid
}

/** Derived amounts for one line (never stored stale, always recomputed). */
export function calcLine(l: { saleRate: number; weight: number; discountPct: number }) {
  const disc = Number.isFinite(l.discountPct) ? l.discountPct : 6
  const less = l.saleRate * (disc / 100)
  const rateAfter = l.saleRate - less
  const saleAmt = l.saleRate * l.weight
  const invAmt = saleAmt * (1 - disc / 100)
  return { less, rateAfter, saleAmt, invAmt }
}

/** Sum the invoice amount (dues) of one invoice. */
export function invoiceTotal(inv: Invoice) {
  return inv.lines.reduce((t, l) => t + calcLine(l).invAmt, 0)
}

// Bumped so a clean install seeds the full pick-lists from the real Excel data.
// v5 REPLACES the Sieve/Size lists with the founder's exact clean lists (in order).
export const SALES_SEED_VERSION = 5

export const SEED_SHAPES = ['ROUND', 'BUGG', 'BUGGET', 'TAPPER', 'TAPP', 'MQ', 'PRINCES', 'PRINCESS', 'PEAR', 'TRILION', 'TRILLION', 'OVAL', 'HEART', 'EMERALD', 'EMRALD', 'CUSHION', 'HALF MOON', 'HALFMOON', 'ROSECUT OVAL', 'ROSECUT PEAR', 'BRIOLET', 'TRAPOZOIDE', 'P/M', 'TAP/BUG']
export const SEED_QUALITIES = ['WH SI', 'WH/SI', 'TOP LB GH SI', 'TOP LC GH SI', 'LB GH SI', 'GH SI', 'GH VS-SI', 'VS-SI-GH', 'WH GH SI', 'LOWER WH', 'WH', 'G+/VS+', 'H/SI1', 'YELLOW', 'BLACK', 'MOH']
export const SEED_CATEGORIES = ['RAPSHODY', 'VEGAS']
// Exact Sieve/Slive list from the founder's "NAZIM SEIVE" sheet, in order.
export const SEED_SIEVES = ['+0000-000', '+000-00', '+00-0', '+0-1', '1-1.5', '1.5-2', '2-2.5', '2.5-3', '3-3.5', '3.5-4', '4-4.5', '4.5-5', '5-5.5', '5.5-6', '6-6.5', '6.6-7', '7-7.5', '7.7-8', '8-8.5', '8.5-9', '9-9.5', '9.5-10', '10-10.5', '10.5-11', '11-11.5', '11.5-12', '12-12.5', '12.5-13', '13-13.5', '13.5-14', '14-14.5', '14.5-15', '15-15.5', '15.5-16', '1/6', '1/5', '1/4', '1/3', '-7', '+7', '-8', '+8', '1/10']
// Exact Size list from the founder's "NAZIM SEIVE" sheet, in order.
export const SEED_SIZES = ['-2', '-6.5', '-11', '+11', '+14', '-7', '+7', '-8', '+8', '+10', '1/6', '1/5', '1/4', '+2-3', '3-3.5', '3.5-4']
export const SEED_CODES = ['PUO', 'SAN', 'MOH', 'DRDRAI', 'DRDRBI', 'DRDRLI', 'DRDRD', 'DMQRAI', 'DTPRAI', 'DPTXX', 'DOVRC', 'PEMLH']

const today = () => new Date().toISOString().slice(0, 10)

interface SalesBookState {
  invoices: Invoice[]
  parties: string[]
  shapes: string[]
  qualities: string[]
  categories: string[]
  sieves: string[]
  sizes: string[]
  codes: string[]
  invoiceSeq: number
  seedVersion: number

  addParty: (p: string) => void
  deleteParty: (p: string) => void                      // founder-only: remove a party + all its invoices
  ensureOpenInvoice: (party: string) => string          // returns the open invoice id (creates if none)
  setInvoiceNo: (invId: string, no: string) => void
  closeInvoice: (invId: string) => void
  reopenInvoice: (invId: string) => void                // founder-only: put a closed invoice back to open for edits
  setInvoicePaid: (invId: string, paid: boolean) => void // mark a closed invoice as payment received / pending
  removeInvoice: (invId: string) => void

  addLine: (invId: string, line: SaleLine) => void
  updateLine: (invId: string, lineId: string, line: SaleLine) => void
  removeLine: (invId: string, lineId: string) => void

  addShape: (s: string) => void
  addQuality: (q: string) => void
  addCategory: (c: string) => void
  addSieve: (v: string) => void
  addSize: (v: string) => void
  addCode: (v: string) => void
  setShapes: (s: string[]) => void
  setQualities: (q: string[]) => void
  setCategories: (c: string[]) => void
  setSieves: (v: string[]) => void
  setSizes: (v: string[]) => void
  setCodes: (v: string[]) => void
  setSeedVersion: (v: number) => void
}

export const useSalesBookStore = create<SalesBookState>()(
  persist(
    (set, get) => ({
      invoices: [],
      parties: [],
      shapes: [],
      qualities: [],
      categories: [],
      sieves: [],
      sizes: [],
      codes: [],
      invoiceSeq: 0,
      seedVersion: 0,

      addParty: (v) => set((s) => (s.parties.includes(v) ? {} : { parties: [...s.parties, v] })),
      deleteParty: (v) => set((s) => ({ parties: s.parties.filter((p) => p !== v), invoices: s.invoices.filter((i) => i.party !== v) })),

      ensureOpenInvoice: (party) => {
        const s = get()
        const open = s.invoices.find((i) => i.party === party && i.status === 'open')
        if (open) return open.id
        const seq = s.invoiceSeq + 1
        const inv: Invoice = {
          id: `inv-${Date.now()}`,
          invoiceNo: `JP-SO-${String(seq).padStart(4, '0')}`,
          party, status: 'open', createdDate: today(), closedDate: '', lines: [],
        }
        set({ invoices: [...s.invoices, inv], invoiceSeq: seq, parties: s.parties.includes(party) ? s.parties : [...s.parties, party] })
        return inv.id
      },
      setInvoiceNo: (invId, no) => set((s) => ({ invoices: s.invoices.map((i) => (i.id === invId ? { ...i, invoiceNo: no } : i)) })),
      closeInvoice: (invId) => set((s) => ({ invoices: s.invoices.map((i) => (i.id === invId ? { ...i, status: 'closed', closedDate: today() } : i)) })),
      reopenInvoice: (invId) => set((s) => ({ invoices: s.invoices.map((i) => (i.id === invId ? { ...i, status: 'open', closedDate: '', paid: false, paidDate: '' } : i)) })),
      setInvoicePaid: (invId, paid) => set((s) => ({ invoices: s.invoices.map((i) => (i.id === invId ? { ...i, paid, paidDate: paid ? today() : '' } : i)) })),
      removeInvoice: (invId) => set((s) => ({ invoices: s.invoices.filter((i) => i.id !== invId) })),

      addLine: (invId, line) => set((s) => ({ invoices: s.invoices.map((i) => (i.id === invId ? { ...i, lines: [...i.lines, line] } : i)) })),
      updateLine: (invId, lineId, line) => set((s) => ({ invoices: s.invoices.map((i) => (i.id === invId ? { ...i, lines: i.lines.map((l) => (l.id === lineId ? line : l)) } : i)) })),
      removeLine: (invId, lineId) => set((s) => ({ invoices: s.invoices.map((i) => (i.id === invId ? { ...i, lines: i.lines.filter((l) => l.id !== lineId) } : i)) })),

      addShape: (v) => set((s) => (s.shapes.includes(v) ? {} : { shapes: [...s.shapes, v] })),
      addQuality: (v) => set((s) => (s.qualities.includes(v) ? {} : { qualities: [...s.qualities, v] })),
      addCategory: (v) => set((s) => (s.categories.includes(v) ? {} : { categories: [...s.categories, v] })),
      addSieve: (v) => set((s) => (s.sieves.includes(v) ? {} : { sieves: [...s.sieves, v] })),
      addSize: (v) => set((s) => (s.sizes.includes(v) ? {} : { sizes: [...s.sizes, v] })),
      addCode: (v) => set((s) => (s.codes.includes(v) ? {} : { codes: [...s.codes, v] })),
      setShapes: (v) => set({ shapes: v }),
      setQualities: (v) => set({ qualities: v }),
      setCategories: (v) => set({ categories: v }),
      setSieves: (v) => set({ sieves: v }),
      setSizes: (v) => set({ sizes: v }),
      setCodes: (v) => set({ codes: v }),
      setSeedVersion: (v) => set({ seedVersion: v }),
    }),
    {
      name: 'hariom-erp-salesbook',
      storage: createJSONStorage(() => sharedStorage),
      partialize: (s) => ({
        invoices: s.invoices, parties: s.parties, shapes: s.shapes, qualities: s.qualities,
        categories: s.categories, sieves: s.sieves, sizes: s.sizes, codes: s.codes,
        invoiceSeq: s.invoiceSeq, seedVersion: s.seedVersion,
      }),
    }
  )
)
